import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  InputNumber,
  Popconfirm,
  Divider,
  Alert,
  Switch,
  Descriptions,
  Dropdown,
  Tooltip,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
  DownOutlined,
  TeamOutlined,
  CodeOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import {
  createWidget,
  updateWidget,
  deleteWidget,
  regenerateWidgetToken,
  getWidgetStatistics,
  getWidgetEmbedCode,
} from '../../services/api';
import SegmentedToggle from '../../components/SegmentedToggle';
import { useChannelScope } from '../../hooks/useChannelScope';
import { useChannelsData } from '../../contexts/ChannelsDataContext';
import { useUserSession } from '../../contexts/UserSessionContext';
import { getDataCollectionsTablePagination } from '../../components/collections/collectionUtils';
import dayjs from 'dayjs';
import '../DataCollections.css';
import { useChannelsFilter } from '../../contexts/ChannelsFilterContext';
import useCloudProviders from '../../hooks/useCloudProviders';
import useLlmModelPicker from '../../hooks/useLlmModelPicker';
import PromptEditor from '../../components/prompt/PromptEditor';
import { getPromptBinding, createPromptVersion, publishPrompt, resolvePromptBinding } from '../../services/api/prompts';
import useDeferredTabPanel from '../../hooks/useDeferredTabPanel';
import WidgetLlmSettingPanel from './components/WidgetLlmSettingPanel';
import { formatDateShort } from './utils/formatters';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function Widgets() {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const { assistantId: filterAssistantId } = useChannelsFilter() || {};
  const { rolesLoaded } = useUserSession();
  const {
    widgets,
    widgetsLoading,
    setWidgets,
    ensureWidgets,
    invalidateWidgets,
  } = useChannelsData();
  const {
    integrationIsAdmin,
    scopeTab,
    setScopeTab,
    assistantById,
    buildScopeTabOptions,
    filterRowsByScope,
    userAssistants,
    platformAssistants,
    assistantsInScope,
  } = useChannelScope();
  const [selectedAssistant, setSelectedAssistant] = useState(null);

  // Widget states
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [widgetStatistics, setWidgetStatistics] = useState(null);
  const [widgetFullTokens, setWidgetFullTokens] = useState(new Map());
  const [embedCode, setEmbedCode] = useState(null);
  const [widgetDetailTab, setWidgetDetailTab] = useState('overview');
  const followupsPanelReady = useDeferredTabPanel('followups', widgetDetailTab);
  const sessionTitlesPanelReady = useDeferredTabPanel('sessionTitles', widgetDetailTab);
  const [customApiUrl, setCustomApiUrl] = useState('');
  const [customWidgetUrl, setCustomWidgetUrl] = useState('');

  const [createWidgetModalVisible, setCreateWidgetModalVisible] = useState(false);
  const [widgetEmbedModalVisible, setWidgetEmbedModalVisible] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);
  const [deleteWidgetLoading, setDeleteWidgetLoading] = useState(false);
  const [createFollowUpEnabled, setCreateFollowUpEnabled] = useState(true);
  const [createSessionTitlesEnabled, setCreateSessionTitlesEnabled] = useState(true);
  const [detailFollowUpEnabled, setDetailFollowUpEnabled] = useState(true);
  const [detailSessionTitlesEnabled, setDetailSessionTitlesEnabled] = useState(true);
  const [followUpSaveLoading, setFollowUpSaveLoading] = useState(false);
  const [sessionTitlesSaveLoading, setSessionTitlesSaveLoading] = useState(false);
  const [configSaveLoading, setConfigSaveLoading] = useState(false);
  const [widgetPromptBody, setWidgetPromptBody] = useState('');
  const [widgetPromptMeta, setWidgetPromptMeta] = useState(null);

  const [createWidgetForm] = Form.useForm();
  const [widgetConfigForm] = Form.useForm();

  const { cloudProviders: widgetCloudProviders } = useCloudProviders(keycloak);

  const createFollowUpPicker = useLlmModelPicker({
    keycloak,
    enabled: createWidgetModalVisible,
    cloudProviders: widgetCloudProviders,
  });
  const createSessionTitlesPicker = useLlmModelPicker({
    keycloak,
    enabled: createWidgetModalVisible,
    cloudProviders: widgetCloudProviders,
  });
  const detailFollowUpPicker = useLlmModelPicker({
    keycloak,
    enabled: Boolean(selectedWidget) && followupsPanelReady,
    cloudProviders: widgetCloudProviders,
  });
  const detailSessionTitlesPicker = useLlmModelPicker({
    keycloak,
    enabled: Boolean(selectedWidget) && sessionTitlesPanelReady,
    cloudProviders: widgetCloudProviders,
  });

  const listLoading = widgetsLoading;

  useEffect(() => {
    if (!keycloak?.authenticated || !rolesLoaded) return;
    ensureWidgets();
  }, [keycloak?.authenticated, rolesLoaded, ensureWidgets]);

  useEffect(() => {
    const assistantId = searchParams.get('assistant_id');
    const action = searchParams.get('action');

    if (assistantId) {
      const aid = parseInt(assistantId, 10);
      const assistant =
        userAssistants.find((a) => a.id === aid) || platformAssistants.find((a) => a.id === aid);
      if (assistant) {
        setSelectedAssistant(assistant);
        if (action === 'create') {
          setCreateWidgetModalVisible(true);
        }
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, userAssistants, platformAssistants, setSearchParams]);

  useEffect(() => {
    setWidgetDetailTab('overview');
  }, [selectedWidget?.id]);

  useEffect(() => {
    if (!createWidgetModalVisible) return;
    setCreateFollowUpEnabled(true);
    setCreateSessionTitlesEnabled(true);
    createFollowUpPicker.reset();
    createSessionTitlesPicker.reset();
  }, [createWidgetModalVisible, createFollowUpPicker.reset, createSessionTitlesPicker.reset]);

  useEffect(() => {
    if (!selectedWidget) return;
    setDetailFollowUpEnabled(selectedWidget.follow_ups_enabled !== false);
    detailFollowUpPicker.hydrateFrom({
      is_local: selectedWidget.follow_ups_is_local,
      model: selectedWidget.follow_ups_model,
      cloud_provider_id: selectedWidget.follow_ups_cloud_provider_id,
    });
  }, [
    selectedWidget?.id,
    selectedWidget?.follow_ups_enabled,
    selectedWidget?.follow_ups_is_local,
    selectedWidget?.follow_ups_model,
    selectedWidget?.follow_ups_cloud_provider_id,
    detailFollowUpPicker.hydrateFrom,
  ]);

  useEffect(() => {
    if (!selectedWidget) return;
    setDetailSessionTitlesEnabled(selectedWidget.session_titles_enabled !== false);
    detailSessionTitlesPicker.hydrateFrom({
      is_local: selectedWidget.session_titles_is_local,
      model: selectedWidget.session_titles_model,
      cloud_provider_id: selectedWidget.session_titles_cloud_provider_id,
    });
  }, [
    selectedWidget?.id,
    selectedWidget?.session_titles_enabled,
    selectedWidget?.session_titles_is_local,
    selectedWidget?.session_titles_model,
    selectedWidget?.session_titles_cloud_provider_id,
    detailSessionTitlesPicker.hydrateFrom,
  ]);

  useEffect(() => {
    if (!selectedWidget || widgetDetailTab !== 'config') return;
    widgetConfigForm.setFieldsValue({
      name: selectedWidget.name,
      is_active: selectedWidget.is_active,
      position: selectedWidget.position,
      primary_color: selectedWidget.primary_color,
      button_size: selectedWidget.button_size,
      button_icon: selectedWidget.button_icon,
      custom_icon_url: selectedWidget.custom_icon_url || undefined,
      avatar_url: selectedWidget.avatar_url || undefined,
      window_title: selectedWidget.window_title,
      placeholder_text: selectedWidget.placeholder_text,
      start_message: selectedWidget.start_message || undefined,
      allow_attachments: selectedWidget.allow_attachments,
      max_attachment_size_mb: selectedWidget.max_attachment_size_mb,
      enable_persistence: selectedWidget.enable_persistence,
      persistence_type: selectedWidget.persistence_type,
      session_timeout_hours: selectedWidget.session_timeout_hours,
      show_branding: selectedWidget.show_branding,
      custom_css: selectedWidget.custom_css || undefined,
      allowed_domains: (selectedWidget.allowed_domains || []).join(', '),
      usage_quota: selectedWidget.usage_quota,
      usage_period: selectedWidget.usage_period,
      expires_at: selectedWidget.expires_at ? dayjs(selectedWidget.expires_at) : null,
    });
  }, [selectedWidget, widgetDetailTab, widgetConfigForm]);

  useEffect(() => {
    const loadWidgetPrompt = async () => {
      if (!selectedWidget || !keycloak?.token) {
        setWidgetPromptBody('');
        setWidgetPromptMeta(null);
        return;
      }
      try {
        const data = await getPromptBinding(keycloak, 'widget', selectedWidget.id, 'system_prompt');
        if (data?.prompt) {
          setWidgetPromptMeta(data.prompt);
          setWidgetPromptBody(data.draft_body || data.live_body || '');
        } else {
          const resolved = await resolvePromptBinding(keycloak, {
            target_type: 'widget',
            target_id: selectedWidget.id,
            slot: 'system_prompt',
            name: `${selectedWidget.name} — Widget instructions`,
            initial_body: '',
          });
          setWidgetPromptMeta(resolved.prompt);
          setWidgetPromptBody(resolved.draft_body || '');
        }
      } catch {
        setWidgetPromptBody('');
      }
    };
    loadWidgetPrompt();
  }, [selectedWidget?.id, keycloak]);

  useEffect(() => {
    if (createWidgetModalVisible && selectedAssistant) {
      createWidgetForm.setFieldsValue({
        assistant_id: selectedAssistant.id,
      });
    }
  }, [createWidgetModalVisible, selectedAssistant, createWidgetForm]);

  useEffect(() => {
    if (selectedWidget) {
      fetchWidgetStats();
    }
  }, [selectedWidget]);

  // Widget handlers
  const fetchWidgets = useCallback(async () => {
    if (!keycloak?.authenticated || !rolesLoaded) return;
    try {
      invalidateWidgets();
      await ensureWidgets(true);
    } catch (error) {
      console.error('Failed to fetch widgets:', error);
    }
  }, [keycloak, rolesLoaded, ensureWidgets, invalidateWidgets]);

  const fetchWidgetStats = async () => {
    if (!selectedWidget) return;
    try {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const data = await getWidgetStatistics(
        keycloak,
        selectedWidget.id,
        thirtyDaysAgo.toISOString(),
        now.toISOString()
      );
      setWidgetStatistics(data);
    } catch (error) {
      console.error('Failed to fetch widget statistics:', error);
    }
  };

  const handleCreateWidget = async (values) => {
    if (createFollowUpEnabled) {
      const err = createFollowUpPicker.validateCloud('cloud follow-up suggestions');
      if (err !== true) {
        message.error(err);
        return;
      }
    }
    if (createSessionTitlesEnabled) {
      const err = createSessionTitlesPicker.validateCloud('cloud session titles');
      if (err !== true) {
        message.error(err);
        return;
      }
    }
    try {
      const widgetData = {
        assistant_id: values.assistant_id,
        name: values.name,
        position: values.position || 'bottom-right',
        primary_color: values.primary_color || '#1890ff',
        button_size: values.button_size || 60,
        start_message: values.start_message || null,
        placeholder_text: values.placeholder_text || 'Type a message...',
        allow_attachments: values.allow_attachments || false,
        enable_persistence: values.enable_persistence !== false,
        window_title: values.window_title || 'Chat with us',
        allowed_domains: values.allowed_domains ? values.allowed_domains.split(',').map(d => d.trim()).filter(d => d) : [],
        follow_ups_enabled: createFollowUpEnabled,
        ...createFollowUpPicker.toWidgetFields('follow_ups'),
        session_titles_enabled: createSessionTitlesEnabled,
        ...createSessionTitlesPicker.toWidgetFields('session_titles'),
      };
      const response = await createWidget(keycloak, widgetData);

      // Store the full token
      setWidgetFullTokens((prev) => new Map(prev).set(response.id, response.full_token));

      message.success('Widget created successfully!');
      setCreateWidgetModalVisible(false);
      createWidgetForm.resetFields();
      await fetchWidgets();

      // Show the embed code modal
      setSelectedWidget(response);
      setEmbedCode(null);
      handleGetEmbedCode(response.id);

    } catch (error) {
      message.error(error.message || 'Failed to create widget');
    }
  };

  const handleSaveWidgetFollowUps = async () => {
    if (!selectedWidget) return;
    if (detailFollowUpEnabled) {
      const err = detailFollowUpPicker.validateCloud('cloud follow-up suggestions');
      if (err !== true) {
        message.error(err);
        return;
      }
    }
    setFollowUpSaveLoading(true);
    try {
      const updated = await updateWidget(keycloak, selectedWidget.id, {
        follow_ups_enabled: detailFollowUpEnabled,
        ...detailFollowUpPicker.toWidgetFields('follow_ups'),
      });
      setSelectedWidget(updated);
      setWidgets((prev) => prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)));
      message.success('Follow-up settings saved');
    } catch (e) {
      message.error(e.message || 'Failed to save follow-up settings');
    } finally {
      setFollowUpSaveLoading(false);
    }
  };

  const handleSaveWidgetSessionTitles = async () => {
    if (!selectedWidget) return;
    if (detailSessionTitlesEnabled) {
      const err = detailSessionTitlesPicker.validateCloud('cloud session titles');
      if (err !== true) {
        message.error(err);
        return;
      }
    }
    setSessionTitlesSaveLoading(true);
    try {
      const updated = await updateWidget(keycloak, selectedWidget.id, {
        session_titles_enabled: detailSessionTitlesEnabled,
        ...detailSessionTitlesPicker.toWidgetFields('session_titles'),
      });
      setSelectedWidget(updated);
      setWidgets((prev) => prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)));
      message.success('Session title settings saved');
    } catch (e) {
      message.error(e.message || 'Failed to save session title settings');
    } finally {
      setSessionTitlesSaveLoading(false);
    }
  };

  const handleSaveWidgetConfig = async (values) => {
    if (!selectedWidget) return;
    setConfigSaveLoading(true);
    try {
      const payload = {
        name: values.name,
        is_active: values.is_active,
        position: values.position,
        primary_color: values.primary_color,
        button_size: values.button_size,
        button_icon: values.button_icon,
        custom_icon_url: values.custom_icon_url || null,
        avatar_url: values.avatar_url || null,
        window_title: values.window_title,
        placeholder_text: values.placeholder_text,
        start_message: values.start_message || null,
        allow_attachments: values.allow_attachments,
        max_attachment_size_mb: values.max_attachment_size_mb,
        enable_persistence: values.enable_persistence,
        persistence_type: values.persistence_type,
        session_timeout_hours: values.session_timeout_hours,
        show_branding: values.show_branding,
        custom_css: values.custom_css || null,
        allowed_domains: values.allowed_domains
          ? String(values.allowed_domains)
              .split(',')
              .map((d) => d.trim())
              .filter(Boolean)
          : [],
        usage_quota: values.usage_quota || null,
        usage_period: values.usage_quota ? (values.usage_period || null) : null,
        expires_at: values.expires_at ? dayjs(values.expires_at).toISOString() : null,
      };
      const updated = await updateWidget(keycloak, selectedWidget.id, payload);
      if (widgetPromptMeta?.id) {
        await createPromptVersion(keycloak, widgetPromptMeta.id, {
          body: widgetPromptBody || '',
          status: 'draft',
        });
        await publishPrompt(keycloak, widgetPromptMeta.id, {});
      }
      setSelectedWidget(updated);
      setWidgets((prev) => prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)));
      message.success('Widget configuration saved');
    } catch (e) {
      message.error(e.message || 'Failed to save configuration');
    } finally {
      setConfigSaveLoading(false);
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await deleteWidget(keycloak, widgetId);
      message.success('Widget deleted successfully');
      fetchWidgets();
      if (selectedWidget?.id === widgetId) {
        setSelectedWidget(null);
      }
    } catch (error) {
      message.error(error.message || 'Failed to delete widget');
      throw error;
    }
  };

  const confirmDeleteWidget = async () => {
    if (!widgetToDelete) return;
    setDeleteWidgetLoading(true);
    try {
      await handleDeleteWidget(widgetToDelete);
      setWidgetToDelete(null);
    } catch {
      /* handleDeleteWidget already shows error */
    } finally {
      setDeleteWidgetLoading(false);
    }
  };

  const handleRegenerateWidgetToken = async (widgetId) => {
    try {
      const response = await regenerateWidgetToken(keycloak, widgetId);
      setWidgetFullTokens(prev => new Map(prev).set(response.id, response.full_token));
      message.success('Widget token regenerated!');
      fetchWidgets();

      Modal.info({
        title: 'New Widget Token',
        width: 600,
        content: (
          <div>
            <Alert
              message="Important: Update your website with the new token!"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Input.Password
              value={response.full_token}
              readOnly
              addonAfter={
                <CopyOutlined onClick={() => copyToClipboard(response.full_token)} style={{ cursor: 'pointer' }} />
              }
            />
          </div>
        ),
      });
    } catch (error) {
      message.error(error.message || 'Failed to regenerate token');
    }
  };

  const handleGetEmbedCode = async (widgetId) => {
    try {
      const data = await getWidgetEmbedCode(keycloak, widgetId);
      setEmbedCode(data);
      setWidgetEmbedModalVisible(true);
    } catch (error) {
      message.error(error.message || 'Failed to get embed code');
    }
  };

  const handleViewSessions = (widget) => {
    setSelectedWidget(widget);
    navigate(`/channels/widgets/${widget.id}/sessions`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard!');
  };

  const filteredWidgets = useMemo(
    () => filterRowsByScope(widgets, filterAssistantId),
    [widgets, filterRowsByScope, filterAssistantId]
  );

  const scopeTabOptions = useMemo(
    () => buildScopeTabOptions(widgets),
    [buildScopeTabOptions, widgets]
  );

  useEffect(() => {
    if (selectedWidget && !filteredWidgets.some((w) => w.id === selectedWidget.id)) {
      setSelectedWidget(null);
    }
  }, [selectedWidget, filteredWidgets]);

  const widgetColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Assistant',
      key: 'assistant',
      render: (_, record) => {
        const assistant = assistantById.get(record.assistant_id);
        return assistant ? assistant.name : 'Unknown';
      },
    },
    ...(integrationIsAdmin && scopeTab !== 'personal'
      ? [
          {
            title: 'Owner',
            key: 'owner',
            ellipsis: true,
            render: (_, record) => (
              <Text ellipsis title={record.owner}>
                {record.owner_username ||
                  (record.owner ? `${record.owner.slice(0, 12)}…` : '—')}
              </Text>
            ),
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Sessions',
      key: 'sessions',
      render: (_, record) => (
        <Space>
          <TeamOutlined />
          <Text>{record.total_sessions} total / {record.active_sessions} active</Text>
        </Space>
      ),
    },
    {
      title: 'Messages',
      dataIndex: 'total_messages',
      key: 'messages',
    },
    {
      title: 'Last Used',
      dataIndex: 'last_used_at',
      key: 'last_used',
      render: (date) => date ? formatDateShort(date) : 'Never',
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      width: 88,
      align: 'center',
      className: 'actions-cell',
      render: (_, record) => {
        const canEditRow = record.user_can_edit !== false;
        const menuItems = [
          {
            key: 'embed',
            label: 'Embed code',
            icon: <CodeOutlined />,
          },
          {
            key: 'details',
            label: 'Details',
            icon: <InfoCircleOutlined />,
          },
          ...(canEditRow
            ? [
                { type: 'divider' },
                {
                  key: 'delete',
                  label: 'Delete widget',
                  danger: true,
                  icon: <DeleteOutlined />,
                },
              ]
            : []),
        ];
        return (
          <div
            className="assistants-actions-toolbar"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Space.Compact>
              <Tooltip title="Sessions">
                <Button
                  type="default"
                  size="small"
                  icon={<TeamOutlined />}
                  aria-label="Sessions"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewSessions(record);
                  }}
                />
              </Tooltip>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: menuItems,
                  onClick: ({ key, domEvent }) => {
                    domEvent?.stopPropagation();
                    if (key === 'embed') handleGetEmbedCode(record.id);
                    else if (key === 'details') setSelectedWidget(record);
                    else if (key === 'delete') setWidgetToDelete(record.id);
                  },
                }}
              >
                <Tooltip title="More actions">
                  <Button
                    type="default"
                    size="small"
                    icon={<DownOutlined />}
                    aria-label="More actions"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Dropdown>
            </Space.Compact>
          </div>
        );
      },
    },
  ];

  // Embed API returns widget_id; use it for token lookup so we still substitute after
  // selectedWidget is cleared (e.g. useEffect runs before the new row is in filteredWidgets).
  const embedModalWidgetId =
    embedCode != null ? embedCode.widget_id ?? selectedWidget?.id : null;
  const embedModalHasFullToken =
    embedModalWidgetId != null && widgetFullTokens.has(embedModalWidgetId);
  const embedModalFullToken = embedModalHasFullToken
    ? widgetFullTokens.get(embedModalWidgetId)
    : undefined;

  const widgetCanEdit = Boolean(selectedWidget && selectedWidget.user_can_edit !== false);
  const configUsageQuota = Form.useWatch('usage_quota', widgetConfigForm);

  return (
    <div className="page-container">
      <div className="collections-page-header" style={{ marginTop: 0 }}>
        <div className="page-header" style={{ flex: 1, minWidth: 0 }}>
          <h2 className="page-header-title">Widgets</h2>
          <span className="page-header-subtitle">Embeddable chat widgets for your websites</span>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="new-collection-btn"
          onClick={() => setCreateWidgetModalVisible(true)}
        >
          Create Widget
        </Button>
      </div>

      <SegmentedToggle options={scopeTabOptions} value={scopeTab} onChange={setScopeTab} />

      <div className="collections-table-wrapper collections-table-animate">
        <Table
          columns={widgetColumns}
          dataSource={filteredWidgets}
          rowKey="id"
          loading={listLoading}
          pagination={getDataCollectionsTablePagination(10, { totalItemLabel: 'widgets' })}
          scroll={{ x: 'max-content' }}
        />
      </div>

      {selectedWidget && (
            <Card style={{ marginTop: 16 }} key={selectedWidget.id}>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={4}>{selectedWidget.name} — Details</Title>
                <Button onClick={() => setSelectedWidget(null)}>Close</Button>
              </Space>

              <SegmentedToggle
                options={[
                  { value: 'overview', text: 'Overview' },
                  { value: 'config', text: 'Configuration' },
                  { value: 'followups', text: 'Follow-ups' },
                  { value: 'sessionTitles', text: 'Session titles' },
                  { value: 'token', text: 'Token' },
                ]}
                value={widgetDetailTab}
                onChange={setWidgetDetailTab}
              />

              {widgetDetailTab === 'overview' && (
                <div style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Statistic title="Total Sessions" value={widgetStatistics?.total_sessions || 0} />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Active Sessions"
                        value={widgetStatistics?.active_sessions || 0}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic title="Total Messages" value={widgetStatistics?.total_messages || 0} />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Avg Latency"
                        value={widgetStatistics?.avg_latency_ms?.toFixed(0) || 0}
                        suffix="ms"
                      />
                    </Col>
                  </Row>
                </div>
              )}

              {widgetDetailTab === 'config' && (
                <div style={{ marginTop: 16 }}>
                  {!widgetCanEdit && (
                    <Alert
                      type="info"
                      showIcon
                      message="View only"
                      description="You do not have permission to change this widget."
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {widgetCanEdit && (
                    <Form
                      form={widgetConfigForm}
                      layout="vertical"
                      onFinish={handleSaveWidgetConfig}
                      key={selectedWidget.id}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="name"
                            label="Widget name"
                            rules={[{ required: true, message: 'Enter a name' }]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="is_active" label="Status" valuePropName="checked">
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="position" label="Position" rules={[{ required: true }]}>
                            <Select>
                              <Option value="bottom-right">Bottom right</Option>
                              <Option value="bottom-left">Bottom left</Option>
                              <Option value="top-right">Top right</Option>
                              <Option value="top-left">Top left</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="primary_color"
                            label="Primary color (hex)"
                            rules={[{ required: true, message: 'Color required' }]}
                          >
                            <Input type="text" maxLength={20} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="button_size"
                            label="Button size (px)"
                            rules={[{ required: true, type: 'number', min: 40, max: 100 }]}
                          >
                            <InputNumber min={40} max={100} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="button_icon" label="Button icon">
                            <Input maxLength={50} placeholder="chat" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="custom_icon_url" label="Custom icon URL">
                            <Input placeholder="Optional" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="avatar_url" label="Avatar image URL">
                            <Input placeholder="Optional" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="window_title" label="Window title" rules={[{ required: true }]}>
                            <Input maxLength={255} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="placeholder_text"
                            label="Input placeholder"
                            rules={[{ required: true, message: 'Required' }]}
                          >
                            <Input maxLength={255} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label="Instructions override (optional)">
                            <PromptEditor
                              value={widgetPromptBody}
                              onChange={setWidgetPromptBody}
                              binding={
                                selectedWidget
                                  ? {
                                      targetType: 'widget',
                                      targetId: selectedWidget.id,
                                      slot: 'system_prompt',
                                    }
                                  : null
                              }
                              basePrompt={
                                assistantsInScope.find((a) => a.id === selectedWidget?.assistant_id)
                                  ? {
                                      label: assistantsInScope.find((a) => a.id === selectedWidget?.assistant_id)?.name,
                                      text: assistantsInScope.find((a) => a.id === selectedWidget?.assistant_id)?.system_prompt || '',
                                    }
                                  : null
                              }
                              placeholder="Override assistant instructions for this widget only"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item name="start_message" label="Start message (optional)">
                            <TextArea rows={2} maxLength={8000} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="allow_attachments"
                            label="Attachments"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="max_attachment_size_mb"
                            label="Max attachment (MB)"
                            rules={[{ required: true, type: 'number', min: 1, max: 25 }]}
                          >
                            <InputNumber min={1} max={25} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="enable_persistence"
                            label="Persistence"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="persistence_type" label="Persistence type" rules={[{ required: true }]}>
                            <Select>
                              <Option value="local">Local</Option>
                              <Option value="server">Server</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="session_timeout_hours"
                            label="Session timeout (hours)"
                            rules={[{ required: true, type: 'number', min: 1, max: 720 }]}
                          >
                            <InputNumber min={1} max={720} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="show_branding"
                            label="Show FocusML branding"
                            valuePropName="checked"
                          >
                            <Switch />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="custom_css" label="Custom CSS (optional)">
                            <TextArea rows={3} maxLength={32000} placeholder="Optional" />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            name="allowed_domains"
                            label="Allowed domains (comma-separated)"
                            extra="Leave empty to allow embedding on any origin (not recommended for production)"
                          >
                            <TextArea rows={2} placeholder="example.com, app.example.com" />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="usage_quota" label="Usage quota (optional)">
                            <InputNumber
                              min={1}
                              style={{ width: '100%' }}
                              placeholder="Unlimited if empty"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            name="usage_period"
                            label="Quota period"
                            dependencies={['usage_quota']}
                          >
                            <Select
                              allowClear
                              placeholder="e.g. month"
                              disabled={!configUsageQuota}
                            >
                              <Option value="day">Day</Option>
                              <Option value="week">Week</Option>
                              <Option value="month">Month</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item name="expires_at" label="Widget expires at (optional)">
                            <DatePicker
                              showTime
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item>
                        <Button type="primary" htmlType="submit" loading={configSaveLoading}>
                          Save configuration
                        </Button>
                      </Form.Item>
                    </Form>
                  )}

                  {!widgetCanEdit && (
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="Position">{selectedWidget.position}</Descriptions.Item>
                      <Descriptions.Item label="Primary Color">
                        <Space>
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              backgroundColor: selectedWidget.primary_color,
                              borderRadius: 4,
                              border: '1px solid #d9d9d9',
                            }}
                          />
                          {selectedWidget.primary_color}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Window Title">{selectedWidget.window_title}</Descriptions.Item>
                      <Descriptions.Item label="Placeholder">{selectedWidget.placeholder_text}</Descriptions.Item>
                      <Descriptions.Item label="Start Message">{selectedWidget.start_message || 'None'}</Descriptions.Item>
                      <Descriptions.Item label="Attachments">
                        {selectedWidget.allow_attachments ? 'Allowed' : 'Disabled'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Persistence">
                        {selectedWidget.enable_persistence ? 'Enabled' : 'Disabled'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Session Timeout">{selectedWidget.session_timeout_hours}h</Descriptions.Item>
                      <Descriptions.Item label="Follow-up suggestions">
                        {selectedWidget.follow_ups_enabled !== false ? 'On' : 'Off'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Follow-up model">
                        {selectedWidget.follow_ups_is_local !== false ? 'Local (Ollama)' : 'Cloud (Vault)'}
                        {(selectedWidget.follow_ups_model || '').trim()
                          ? ` — ${(selectedWidget.follow_ups_model || '').trim()}`
                          : ' — default (assistant chat model)'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Session list titles (AI)">
                        {selectedWidget.session_titles_enabled !== false ? 'On' : 'Off'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Session title model">
                        {selectedWidget.session_titles_is_local !== false ? 'Local (Ollama)' : 'Cloud (Vault)'}
                        {(selectedWidget.session_titles_model || '').trim()
                          ? ` — ${(selectedWidget.session_titles_model || '').trim()}`
                          : ' — platform default or assistant chat model'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Allowed Domains" span={2}>
                        {selectedWidget.allowed_domains?.length > 0 ? (
                          selectedWidget.allowed_domains.map((d) => <Tag key={d}>{d}</Tag>)
                        ) : (
                          <Text type="secondary">All domains (not recommended)</Text>
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  )}

                  {widgetCanEdit && (
                    <Descriptions
                      title="Read-only: follow-ups and session titles"
                      style={{ marginTop: 24 }}
                      bordered
                      column={1}
                    >
                      <Descriptions.Item label="Follow-up suggestions (edit in Follow-ups tab)">
                        {selectedWidget.follow_ups_enabled !== false ? 'On' : 'Off'}
                        {' '}
                        —
                        {selectedWidget.follow_ups_is_local !== false ? ' Local' : ' Cloud'}
                        {(selectedWidget.follow_ups_model || '').trim()
                          ? ` — ${(selectedWidget.follow_ups_model || '').trim()}`
                          : ''}
                      </Descriptions.Item>
                      <Descriptions.Item label="Session titles (edit in Session titles tab)">
                        {selectedWidget.session_titles_enabled !== false ? 'On' : 'Off'}
                        {' '}
                        —
                        {selectedWidget.session_titles_is_local !== false ? ' Local' : ' Cloud'}
                        {(selectedWidget.session_titles_model || '').trim()
                          ? ` — ${(selectedWidget.session_titles_model || '').trim()}`
                          : ''}
                      </Descriptions.Item>
                    </Descriptions>
                  )}
                </div>
              )}

              {widgetDetailTab === 'followups' && (
                <div style={{ marginTop: 16 }}>
                  {followupsPanelReady ? (
                    <WidgetLlmSettingPanel
                      alertProps={{
                        message: 'Suggested follow-up questions',
                        description:
                          "After each assistant reply, the widget can show short follow-up chips. Configure the LLM used only for that step (defaults to the assistant’s chat model when no model is set).",
                      }}
                      enabled={detailFollowUpEnabled}
                      onEnabledChange={setDetailFollowUpEnabled}
                      enabledLabel="Enable follow-up suggestions"
                      picker={detailFollowUpPicker}
                      cloudProviders={widgetCloudProviders}
                      navigate={navigate}
                      sectionTitle="Follow-up LLM"
                      modelStepLabel="Model for follow-up suggestions"
                      disabled={!widgetCanEdit}
                      saveLoading={followUpSaveLoading}
                      onSave={handleSaveWidgetFollowUps}
                      saveLabel="Save follow-up settings"
                    />
                  ) : (
                    <div style={{ padding: 32, textAlign: 'center' }}>
                      <Spin />
                    </div>
                  )}
                </div>
              )}

              {widgetDetailTab === 'sessionTitles' && (
                <div style={{ marginTop: 16 }}>
                  {sessionTitlesPanelReady ? (
                    <WidgetLlmSettingPanel
                      alertProps={{
                        message: 'Session list in Integrations',
                        description:
                          'After the first user and assistant message, we generate a short title and line for the full-screen sessions view. Leave the model empty here to use the platform default from App Settings; if that is also empty, the assistant’s chat model is used. Use a small, fast model to keep latency and cost down.',
                      }}
                      enabled={detailSessionTitlesEnabled}
                      onEnabledChange={setDetailSessionTitlesEnabled}
                      enabledLabel="Enable session titles"
                      picker={detailSessionTitlesPicker}
                      cloudProviders={widgetCloudProviders}
                      navigate={navigate}
                      sectionTitle="Session title LLM"
                      modelStepLabel="Model for session titles"
                      disabled={!widgetCanEdit}
                      saveLoading={sessionTitlesSaveLoading}
                      onSave={handleSaveWidgetSessionTitles}
                      saveLabel="Save session title settings"
                    />
                  ) : (
                    <div style={{ padding: 32, textAlign: 'center' }}>
                      <Spin />
                    </div>
                  )}
                </div>
              )}

              {widgetDetailTab === 'token' && (
                <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                  <Alert
                    message="Widget Token"
                    description="Use this token to authenticate your widget. Keep it secret!"
                    type="info"
                    showIcon
                  />
                  <Space>
                    <Text code>{selectedWidget.masked_token}</Text>
                    <Popconfirm
                      title="Regenerate token? Your current embed code will stop working."
                      onConfirm={() => handleRegenerateWidgetToken(selectedWidget.id)}
                    >
                      <Button icon={<ReloadOutlined />} disabled={!widgetCanEdit}>
                        Regenerate
                      </Button>
                    </Popconfirm>
                  </Space>
                </Space>
              )}
            </Card>
          )}

      {/* Create Widget Modal */}
      <Modal
        title="Create Chat Widget"
        open={createWidgetModalVisible}
        onCancel={() => {
          setCreateWidgetModalVisible(false);
          createWidgetForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={createWidgetForm}
          layout="vertical"
          onFinish={handleCreateWidget}
          initialValues={{
            position: 'bottom-right',
            primary_color: '#1890ff',
            button_size: 60,
            placeholder_text: 'Type a message...',
            window_title: 'Chat with us',
            enable_persistence: true,
            session_timeout_hours: 24,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="assistant_id"
                label="Assistant"
                rules={[{ required: true, message: 'Please select an assistant' }]}
              >
                <Select placeholder="Select an assistant">
                  {assistantsInScope.map((assistant) => (
                    <Option key={assistant.id} value={assistant.id}>
                      {assistant.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Widget Name"
                rules={[{ required: true, message: 'Please enter a name' }]}
              >
                <Input placeholder="e.g., Support Chat" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Appearance</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="position" label="Button Position">
                <Select>
                  <Option value="bottom-right">Bottom Right</Option>
                  <Option value="bottom-left">Bottom Left</Option>
                  <Option value="top-right">Top Right</Option>
                  <Option value="top-left">Top Left</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="primary_color" label="Primary Color">
                <Input type="color" style={{ width: '100%', height: 32 }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="button_size" label="Button Size (px)">
                <InputNumber min={40} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="window_title" label="Window Title">
                <Input placeholder="Chat with us" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="placeholder_text" label="Input Placeholder">
                <Input placeholder="Type a message..." />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Behavior</Divider>

          <Form.Item name="start_message" label="Welcome Message">
            <TextArea
              rows={3}
              placeholder="Hello! How can I help you today?"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="allow_attachments" label="Allow Attachments" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="enable_persistence" label="Session Persistence" valuePropName="checked">
                <Switch defaultChecked />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="session_timeout_hours" label="Session Timeout (hours)">
                <InputNumber min={1} max={720} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Follow-up suggestions</Divider>
          <WidgetLlmSettingPanel
            alertProps={{
              message:
                'Optional: use a different LLM only for the short follow-up chips after each reply. Leave the model empty to use the assistant’s normal chat model.',
            }}
            enabled={createFollowUpEnabled}
            onEnabledChange={setCreateFollowUpEnabled}
            enabledLabel="Enable follow-up suggestions"
            picker={createFollowUpPicker}
            cloudProviders={widgetCloudProviders}
            navigate={navigate}
            sectionTitle="Follow-up LLM"
            modelStepLabel="Model for follow-up suggestions"
          />

          <Divider>Session titles (admin list)</Divider>
          <WidgetLlmSettingPanel
            alertProps={{
              message:
                'Optional: small model to label sessions in the full-screen session viewer. Leave empty to use the platform default (App Settings); if that is also empty, the assistant’s chat model is used.',
            }}
            enabled={createSessionTitlesEnabled}
            onEnabledChange={setCreateSessionTitlesEnabled}
            enabledLabel="Enable session titles"
            picker={createSessionTitlesPicker}
            cloudProviders={widgetCloudProviders}
            navigate={navigate}
            sectionTitle="Session title LLM"
            modelStepLabel="Model for session titles"
          />

          <Divider>Security</Divider>

          <Form.Item
            name="allowed_domains"
            label="Allowed Domains"
            extra="Comma-separated list of domains where widget can be embedded (e.g., example.com, app.example.com)"
          >
            <TextArea
              rows={2}
              placeholder="example.com, app.example.com"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block size="large">
              Create Widget
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Widget Embed Code Modal */}
      <Modal
        title="Embed Widget"
        open={widgetEmbedModalVisible}
        onCancel={() => setWidgetEmbedModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setWidgetEmbedModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {embedCode && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Show token if available */}
            {embedModalHasFullToken ? (
              <Alert
                message="Your Widget Token"
                description={
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>This token has been automatically inserted into the embed code below:</Text>
                    <Input.Password
                      value={embedModalFullToken}
                      readOnly
                      addonAfter={
                        <CopyOutlined
                          onClick={() => copyToClipboard(embedModalFullToken)}
                          style={{ cursor: 'pointer' }}
                        />
                      }
                    />
                  </Space>
                }
                type="success"
                showIcon
              />
            ) : (
              <Alert
                message="Widget Token Required"
                description="Replace 'YOUR_WIDGET_TOKEN' in the code below with the token you received when creating this widget."
                type="warning"
                showIcon
              />
            )}

            <Alert
              message="Installation Instructions"
              description={
                <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                  {embedCode.instructions?.map((instruction, i) => (
                    <li key={i}>{instruction}</li>
                  ))}
                </ol>
              }
              type="info"
              showIcon
            />

            <Card title="URL Configuration (Optional)" size="small">
              <Row gutter={16}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Widget Script URL</Text>
                  <Input
                    placeholder="e.g., https://yourdomain.com"
                    value={customWidgetUrl}
                    onChange={(e) => setCustomWidgetUrl(e.target.value)}
                    style={{ marginTop: 4 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>Where the widget JS file is hosted</Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: 12 }}>API Base URL</Text>
                  <Input
                    placeholder="e.g., https://yourdomain.com/api"
                    value={customApiUrl}
                    onChange={(e) => setCustomApiUrl(e.target.value)}
                    style={{ marginTop: 4 }}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>Your backend API endpoint</Text>
                </Col>
              </Row>
            </Card>

            <Card title="Embed Code" size="small">
              <pre style={{
                background: '#f5f5f5',
                padding: 16,
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
                margin: 0,
                fontSize: 12
              }}>
                {(() => {
                  let code = embedCode.embed_code;
                  // Replace token if available
                  if (embedModalHasFullToken && embedModalFullToken) {
                    code = code.replace('YOUR_WIDGET_TOKEN', embedModalFullToken);
                  }
                  // Replace URLs if custom values provided
                  if (customWidgetUrl) {
                    code = code.replace(/w\.src = '[^']+\/widget\/focusml-chat\.js'/, `w.src = '${customWidgetUrl}/widget/focusml-chat.js'`);
                  }
                  if (customApiUrl) {
                    code = code.replace(/data-api-base', '[^']+'\)/, `data-api-base', '${customApiUrl}')`);
                  }
                  return code;
                })()}
              </pre>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={() => {
                  let code = embedCode.embed_code;
                  if (embedModalHasFullToken && embedModalFullToken) {
                    code = code.replace('YOUR_WIDGET_TOKEN', embedModalFullToken);
                  }
                  if (customWidgetUrl) {
                    code = code.replace(/w\.src = '[^']+\/widget\/focusml-chat\.js'/, `w.src = '${customWidgetUrl}/widget/focusml-chat.js'`);
                  }
                  if (customApiUrl) {
                    code = code.replace(/data-api-base', '[^']+'\)/, `data-api-base', '${customApiUrl}')`);
                  }
                  copyToClipboard(code);
                }}
                style={{ marginTop: 16 }}
              >
                Copy Code
              </Button>
            </Card>
          </Space>
        )}
      </Modal>

      <Modal
        title="Delete this widget?"
        open={widgetToDelete != null}
        onOk={confirmDeleteWidget}
        onCancel={() => setWidgetToDelete(null)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        confirmLoading={deleteWidgetLoading}
      >
        <p>This widget and its configuration will be permanently removed.</p>
      </Modal>
    </div>
  );
}

